import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import mozLogo from '../../assets/images/moz.svg';
import TooltipIcon from '../tooltip_icon';
import ShareButtons from './card/share_buttons';
import FavoriteButton from '../favorite_button';
import vlog from '../../javascript/vlog';

function TrafficStats({
  sourceRequestId,
  typeText,
  publicationLogoUrl,
  shareText,
  shareUrl,
  requestUrl,
  favoriteableId,
  favoriteableType,
  favoriteId,
  favorited,
  ahoyObjectGid,
  paid,
}) {
  const [loading, setLoading] = useState(true);
  const [domainAuthority, setDomainAuthority] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchDomainAuthority = async () => {
      if (!window.supabaseClient) {
        setDomainAuthority(null);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await window.supabaseClient
          .from('requests')
          .select('media_outlet_domain_authority')
          .eq('migrate_id', sourceRequestId)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          vlog.warn('Domain authority lookup failed:', error.message);
        }

        setDomainAuthority(data?.media_outlet_domain_authority ?? null);
      } catch (err) {
        if (cancelled) return;
        vlog.warn('Domain authority lookup failed:', err.message);
        setDomainAuthority(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDomainAuthority();
    return () => { cancelled = true; };
  }, [sourceRequestId]);

  return (
    <div className="traffic-stats-container">
      {/* Request Type and Social Share Icons Row */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="traffic-stats-request-type text-uppercase text-muted">
          {typeText}
        </div>
        <div className="d-flex align-items-center">
          {shareUrl && (
            <ShareButtons
              displayShareText={false}
              shareText={shareText}
              shareUrl={shareUrl}
              requestUrl={requestUrl}
            />
          )}
          {favoriteableId && (
            <>
              <div className="border-end traffic-stats-social-divider" />
              <FavoriteButton
                favoriteableId={favoriteableId}
                favoriteableType={favoriteableType}
                favoriteId={favoriteId}
                favorited={favorited}
                ahoyObjectGid={ahoyObjectGid}
                size="sm"
                icon="fa-heart"
              />
            </>
          )}
        </div>
      </div>

      {paid && (
        <div className="mb-3 traffic-stats-request-type">
          <strong className="text-danger"> (FEE-BASED OPPORTUNITY) </strong>
          <TooltipIcon
            iconClass="fa fa-circle-info"
            tooltipText="Sometimes called pay-to-play, fee-based opportunities are paid placements used to gain exposure, or access media platforms—often for marketing, brand visibility, or thought leadership."
          />
        </div>
      )}

      <div className="d-flex gap-4">
        {/* Publication Logo - Left Side */}
        <div className="traffic-stats-pub-logo-wrapper">
          {publicationLogoUrl && (
            <img
              src={publicationLogoUrl}
              alt="Publication Logo"
              className="traffic-stats-pub-logo"
            />
          )}
        </div>

        {/* Metrics Cards - Right Side */}
        <div className="traffic-stats-metrics d-flex gap-3 flex-grow-1 justify-content-end">
          {/* Domain Authority Card */}
          <div className="traffic-stat-card">
            <div className="d-flex align-items-center mb-2">
              <i className="fa-light fa-magnifying-glass me-2" />
              <span className="stat-title">Domain Authority</span>
              <TooltipIcon
                iconClass="fa-light fa-circle-info ms-1 text-muted"
                tooltipText="Domain Authority (DA) is a score that shows how likely a website is to rank on search engines. A higher DA means better chances of ranking higher, attracting more traffic, and gaining visibility"
              />
            </div>
            {loading ? (
              <div className="stat-value-large mb-1">
                <i className="fa-solid fa-spinner fa-spin" />
              </div>
            ) : (
              <div className="stat-value-large mb-1">
                {domainAuthority != null ? (
                  domainAuthority
                ) : (
                  <span className="text-muted" title="Domain Authority data is not yet available for this publication">N/A</span>
                )}
              </div>
            )}
            <div className="stat-powered-by">
              Powered by <img src={mozLogo} alt="Moz" className="powered-by-logo" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

TrafficStats.propTypes = {
  sourceRequestId: PropTypes.number,
  typeText: PropTypes.string,
  publicationLogoUrl: PropTypes.string,
  shareText: PropTypes.string,
  shareUrl: PropTypes.string,
  requestUrl: PropTypes.string,
  favoriteableId: PropTypes.number,
  favoriteableType: PropTypes.string,
  favoriteId: PropTypes.number,
  favorited: PropTypes.bool,
  ahoyObjectGid: PropTypes.string,
  paid: PropTypes.bool,
};

TrafficStats.defaultProps = {
  sourceRequestId: undefined,
  typeText: undefined,
  publicationLogoUrl: undefined,
  shareText: undefined,
  shareUrl: undefined,
  requestUrl: undefined,
  favoriteableId: undefined,
  favoriteableType: undefined,
  favoriteId: undefined,
  favorited: false,
  ahoyObjectGid: undefined,
  paid: false,
};

export default TrafficStats;
